import { pathnames } from './src/i18n/pathnames';

export const SITE_CONFIG = {
    name: 'ACEB',
    fullName: 'ACEB.com',
    url: process.env.NEXT_PUBLIC_URL as string,
    domain: 'aceb.com',

    brand: {
        tagline: 'Digital Gift Cards for Everyone',
        description: 'Buy digital gift cards from top brands with cryptocurrency. Instant delivery, secure payment, and great discounts.',
    },

    metadata: {
        defaultTitle: 'ACEB Gift Cards - Digital Gift Cards for Everyone',
        titleTemplate: '%s | ACEB.com',
        description: 'Buy digital gift cards from top brands. Instant delivery, secure payment, and great discounts.',
        keywords: '', //'gift cards, digital gift cards, crypto gift cards, bitcoin gift cards, instant delivery, buy gift cards online',

        openGraph: {
            type: 'website',
            locale: 'en_US',
            siteName: 'ACEB.com',
            images: [
                {
                    url: '/og-image.png',
                    width: 1200,
                    height: 630,
                    alt: 'ACEB - Digital Gift Cards',
                }
            ],
        },

        twitter: {
            cardType: 'summary_large_image',
            site: '@aceb',
            creator: '@aceb',
        },
    },

    contact: {
        email: 'support@aceb.com',
        supportUrl: '/contact',
    },

    legal: {
        companyName: 'LOYALTY VAULT SRL',
        cui: '51229859',
        regCom: 'J2025007615002',
        address: 'Bdul. Unirii 32 Bl. 73B Et. 2 Ap. 10',
        postalCode: '130082',
        city: 'Targoviste',
        county: 'Dambovita',
        country: 'Romania',
    },

    social: {
        facebook: 'https://www.facebook.com/acebgiftcards',
        twitter: 'https://x.com/ACEBgiftcards',
        instagram: 'https://www.instagram.com/acebcom',
        youtube: 'https://www.youtube.com/@acebcom',
        tiktok: 'https://www.tiktok.com/@acebgiftcards',
        telegram: 'https://t.me/acebgiftcards',
        github: 'https://github.com/acebcom/aceb.com',
    },

    imageCdnUrls: (process.env.NEXT_PUBLIC_IMAGE_CDN_URLS || '').split(',').map(u => u.trim()).filter(Boolean),

    product: {
        minDenominationStep: 1,
    },

    features: {
        analytics: {
            googleTagContainer: process.env.NEXT_PUBLIC_GOOGLE_TAG_CONTAINER_ID,
            gtmId: process.env.NEXT_PUBLIC_GTM_ID,
            ga4MeasurementIds: process.env.NEXT_PUBLIC_GA4_MEASUREMENT_IDS,
            googleAds: {
                ids: process.env.NEXT_PUBLIC_GOOGLE_ADS_IDS,
                conversionLabels: process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_LABELS,
            },
            ahrefs: process.env.NEXT_PUBLIC_AHREFS_TOKEN,
            clarity: process.env.NEXT_PUBLIC_CLARITY_ID,
            metaPixel: {
                ids: process.env.NEXT_PUBLIC_META_PIXEL_IDS,
            },
            matomo: {
                url: process.env.NEXT_PUBLIC_MATOMO_URL,
                siteId: process.env.NEXT_PUBLIC_MATOMO_SITE_ID,
            },
        },
    },

    review: {
        trustpilotUrl: 'https://www.trustpilot.com/review/aceb.com',
        orderCompletedRuleTtlInSeconds: 300,
    },

    google: {
        // Google accepts a whole domain only, so every environment links the production one.
        preferredSourceUrl: 'https://www.google.com/preferences/source?q=aceb.com',
    },

    /**
     * What theme to use for visitors who haven't picked one yet (no `theme` cookie).
     *   'system' - follow the user's OS preference via `prefers-color-scheme`.
     *   'light'  - always start in light mode.
     *   'dark'   - always start in dark mode.
     * The user can still override via the toggle, which writes their choice to
     * a cookie and takes precedence on subsequent visits.
     */
    defaultTheme: 'system' as 'system' | 'light' | 'dark',
} as const;

export const ESSENTIAL_COOKIES = [
    'NEXT_LOCALE',
    'selectedCountry',
    'selectedCurrency',
    'countries_cache',
    // cf related ?
    'cf_clearance',
    '__cf_bm',
    '__cfruid',
];

// Valid first path segments (after locale stripping) used by the routing
// middleware (proxy.ts) to separate real routes from junk URLs. Auto-derived
// from the i18n `pathnames` map so it stays in sync as routes are added, plus
// routes not declared there (OAuth callbacks, the 410/404 pages). Any single
// root segment NOT in this set returns a hard 404 instead of being rewritten
// into the [locale] route and 307-redirected to "/" (which Google reads as an
// effectively infinite supply of soft-404s).
//
// ⚠️ Adding a new top-level route declared in pathnames.ts is picked up
// automatically. Adding an UNPREFIXED route that is NOT in pathnames.ts (e.g.
// another OAuth provider, a new special page) requires adding its first
// segment here too - otherwise the middleware will 404 it.
export const KNOWN_TOP_LEVEL_SEGMENTS = new Set<string>([
    ...Object.keys(pathnames).map((p) => p.split('/')[1]),
    'google',
    'facebook',
    'github',
    'twitter',
    'gone',
    '404',
]);

export const global = {
    titleSuffix: false,
    h1Suffix: ' | ACEB.com',
    metaTitleSuffix: ' | ACEB.com',
    cardLogoAltSuffix: ' Gift Card'
}

export function getTitleSuffix() {
    return global.titleSuffix ? ` ${global.titleSuffix}` : '';
}

export function getH1Suffix() {
    return global.h1Suffix ? ` ${global.h1Suffix}` : '';
}

export function getMetaTitleSuffix(metaTitle: string) {
    if (metaTitle.endsWith(global.metaTitleSuffix)) {
        return metaTitle;
    }
    return `${global.metaTitleSuffix}`;
}

export function getCardLogoAltSuffix() {
    return global.cardLogoAltSuffix;
}

export const ORDER_LIMITS = {
    minOrderUsd: 1,
    minProductValueUsd: 1
} as const;

// Mirror of aceb-api `appConstants.referralCodes`. The backend is the source
// of truth and enforces these; the FE uses them only to show/hide UI
// affordances. Keep in sync with the backend constant when changing values.
export const REFERRAL_CONFIG = {
    usernameEnabled: true,
    randomEnabled: true,
    customEnabled: true,
    // Max RANDOM codes a user may hold (counts the auto-created default).
    maxPerUser: 3,
    // TEMPORARY: hides the user-facing "Create link" and "Delete" code buttons
    // in the account referrals section. Flip back to true to restore them.
    linkManagementEnabled: false,
} as const;

// PDP "Choose amount" default. Interpreted in the variant's NATIVE currency
// - i.e. $50 for a USD variant, €50 for a EUR variant, £50 for a GBP variant.
// No FX conversion. For range variants we clamp to [min, max] and snap to
// denomination_step. For fixed-denomination variants the value is forced to
// the denomination, so this is only the picker preference.
export const DEFAULT_PDP_AMOUNT = 50;

// Gift card listing page size. The server component fetches with this and the
// client mirrors it, so the grid, the per-page select and the pagination can
// never disagree about how many cards a page holds. The per-page select tops
// out at MAX; a bigger ?limit= is hand-typed and gets clamped.
export const LISTING_DEFAULT_ITEMS_PER_PAGE = 60;
export const LISTING_MAX_ITEMS_PER_PAGE = 100;

// Smooth-scroll the window to the top on every client-side route change.
// Next.js already resets scroll on most forward navigations, but this
// enforces consistent behavior (incl. hash-less re-navigations).
export const SCROLL_TO_TOP_ON_NAVIGATE = true;

// Fraction of pageviews that report Web Vitals RUM to /api/vitals (ACEB-219).
// 1 = every pageview, 0 = disabled; dial down if PostHog event volume grows.
export const WEB_VITALS_SAMPLE_RATE = 1;

export const RAFFLE_CONFIG = {
    minCartValueUsd: 50,
    prizeValueUsd: 100,
} as const;

// export const HIDE_VARIANT_COUNTRY_WHEN_IDENTICAL = true;

export const DEFAULT_CURRENCY = 'USD';

export const currencies = [
    { code: 'USD', symbol: '$', label: 'US Dollar' },
    { code: 'EUR', symbol: '€', label: 'Euro' },
    { code: 'GBP', symbol: '£', label: 'British Pound' },
    { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar' },
];

export interface PaymentLogo {
    name: string;
    file: string;

    // optional link to custom pages
    pageSlug?: string;
}

// The coin and app lists below are mirrored by aceb-api
// `appConstants.agent.cryptoPaymentMethods`, which tells AI agents what each
// tile accepts. Change a list here, change it there.
//
// The classic crypto tile + the homepage/product "we accept" walls. Exactly
// the coins a shopper can PAY with at CoinGate checkout: their live list
// (GET /v2/currencies?native=true&enabled=true, 2026-09-29) filtered on
// merchant.pay=true. Listed-but-unpayable there (pay=false): BCH, SHIB,
// ATOM, ARB, EGLD, FLOW, BTTC, AVA, BUSD, WBNB - never show those.
// (Bitcoin Cash was on this list for months while unpayable.)
export const CRYPTO_LOGOS: PaymentLogo[] = [
    { name: 'Bitcoin', file: 'btc.png', pageSlug: 'buy-gift-cards-with-bitcoin-btc' },
    { name: 'Ethereum', file: 'eth.png', pageSlug: 'buy-gift-cards-with-ethereum' },
    { name: 'Litecoin', file: 'litecoin.png', pageSlug: 'buy-gift-cards-with-litecoin' },
    // { name: 'USDT', file: 'usdt.webp' }, // USDT not available anymore in EU
    { name: 'USDC', file: 'usdc.webp', pageSlug: 'buy-gift-cards-with-usdc' },
    { name: 'Solana', file: 'solana.png', pageSlug: 'buy-gift-cards-with-solana' },
    { name: 'BNB', file: 'bnb.png', pageSlug: 'buy-gift-cards-with-bnb' },
    { name: 'XRP', file: 'xrp.png', pageSlug: 'buy-gift-cards-with-xrp' },
    { name: 'Polygon', file: 'polygon.png', pageSlug: 'buy-gift-cards-with-polygon' },
    { name: 'Dogecoin', file: 'dogecoin.png', pageSlug: 'buy-gift-cards-with-dogecoin' },
    { name: 'Tron', file: 'tron.png', pageSlug: 'buy-gift-cards-with-tron' },
    { name: 'EURC', file: 'eurc.png' },
];

// The stablecoin checkout tile (served by Triple-A - provider never shown).
// Their FULL coin list (support KB, 2026-09-29) is exactly USDT, USDC,
// PYUSD, BTC and ETH - multi-network (USDT on 5 chains, BTC incl.
// Lightning) but no further coins. Plus the exchange apps their form pays
// from. No pageSlug on purpose: no payment-method landing pages for these.
//
// Two faces. Marketed: "Pay with USDT & USDC" (USDC steered here, away from
// the classic tile; BTC/ETH belong to the classic tile). Full: everything the
// provider takes, shown when the classic tile is closed.
export const STABLECOIN_LOGOS: PaymentLogo[] = [
    { name: 'USDT', file: 'usdt.webp' },
    { name: 'USDC', file: 'usdc.webp' },
];

export const STABLECOIN_LOGOS_FULL: PaymentLogo[] = [
    { name: 'USDT', file: 'usdt.webp' },
    { name: 'USDC', file: 'usdc.webp' },
    { name: 'PYUSD', file: 'pyusd.png' },
    { name: 'Bitcoin', file: 'btc.png' },
    { name: 'Ethereum', file: 'eth.png' },
];

// Apps/wallet connectors each crypto checkout accepts payments from,
// rendered as wide SEPA-style badges (not coin circles) on the tiles.
export const CRYPTO_APP_LOGOS: PaymentLogo[] = [
    { name: 'Binance Pay', file: 'binance_pay.png' },
    { name: 'Wallet Connect', file: 'wallet_connect.png' },
];

// App badges temporarily off (2026-09-30).
export const STABLECOIN_APP_LOGOS: PaymentLogo[] = [
    // { name: 'Crypto.com', file: 'crypto_com_pay.png' },
];

export const STABLECOIN_APP_LOGOS_FULL: PaymentLogo[] = [
    // { name: 'Binance Pay', file: 'binance_pay.png' },
    // { name: 'Crypto.com', file: 'crypto_com_pay.png' },
];

export const PAYMENT_METHOD_LOGOS: PaymentLogo[] = [
    { name: 'Binance Pay', file: 'binance_pay.png', pageSlug: 'buy-gift-cards-with-binance-pay' },
    { name: 'SEPA', file: 'svgs/sepa.svg', pageSlug: 'buy-gift-cards-with-sepa' },
    { name: 'Wallet Connect', file: 'wallet_connect.png', pageSlug: 'buy-gift-cards-with-walletconnect' },
    { name: 'CoinGate', file: 'coingate.png' },
];

export const PAYMENT_METHOD_PAGE_SLUGS: string[] = Array.from(
    new Set(
        [...CRYPTO_LOGOS, ...PAYMENT_METHOD_LOGOS]
            .map((logo) => logo.pageSlug)
            .filter((slug): slug is string => Boolean(slug)),
    ),
);

export const PAYMENT_METHOD_PAGES_CACHE_TTL_MS = 60 * 60 * 1000;

// Identity-verification (KYC) modal: how often the cart asks the backend for
// the verdict while the shopper is on the phone, and for how long.
export const KYC_POLL = {
  intervalMs: 5_000,
  maxMs: 10 * 60 * 1000,
};

export const BANK_TRANSFER_DETAILS = {
    receiverName: 'LOYALTY VAULT SRL',
    swift: 'BTRLRO22XXX',
    beneficiaryBank: 'Banca Transilvania',
    iban: 'RO83BTRLEURCRT0692951101',
    // Must stay in sync with appConstants.bankTransfer.remittancePrefix - the
    // API encodes the same string into the EPC QR for this order.
    remittancePrefix: 'aceb.com gift card order',
} as const;
